import { Container, Row, Col, size, Badge } from '@everlywell/leaves';
import AnchorLink from 'components/AnchorLink';
import { Breadcrumbs } from 'components/Breadcrumbs';
import MeasuredItemsModal from 'components/MeasuredItemsModal';
import PDPSwiperWrapper from 'components/PDP/ProducSwiperWrapper';
import Pricing from 'components/Pricing';
import { Theme, Themable } from 'components/Themable';
import React, { useEffect, useState, useRef, useContext } from 'react';
import { useInView } from 'react-intersection-observer';
import ewAnalytics from 'utils/analytics';
import { ANALYTICS } from 'utils/constants/analytics';
import { PDP } from 'utils/constants/dataTest';
import { sanitize } from 'utils/domHelpers';
import { Promo } from 'utils/helpers/activePromoHelpers';
import {
  ComparisonModalContext,
  ProductInfoContext,
} from 'utils/helpers/pdpContexts';
import useProductCategories from 'utils/hooks/useProductCategories';
import { ProductBadgeConfig, ProductCategory } from 'utils/types';

import { DisclaimerDataProps } from '../../../components/GeneralDisclaimer';
import { ScreenerDataProps } from '../../../components/PrePurchaseScreener';
import { SubscriptionOptionType } from '../../../containers/PDP/HeroContainer';
import lightsLeft from '../../../images/themes/Winter2020/lights-left.svg';
import lightsRight from '../../../images/themes/Winter2020/lights-right.svg';
import PurchaseControls, { PurchaseType } from './PurchaseControls';
import star from './star.svg';
import * as S from './styles';

export type PDPHeroProps = {
  productId: number;
  name: string;
  price: string;
  memberPrice?: string;
  memberHeroCopy?: string;
  membershipVariantId: number;
  activeCredits?: number;
  descriptionHeader: string;
  description: string;
  averageRating: number;
  reviewCount: number;
  sliderImages: {
    image: string | React.ReactNode;
    imageAlt: string;
    id?: string;
  }[];
  collectionMethods: Array<{ iconUrl: string; name: string }>;
  measurements: Array<{ iconUrl: string; name: string }>;
  subscriptionOptions: SubscriptionOptionType[];
  onSubscriptionOptionChange: (optionName: string) => void;
  onAddToCartClick: (
    e: React.FormEvent<HTMLButtonElement>,
    callback?: () => void,
  ) => void;
  onExpressPayClick: (callback: () => void) => void;
  onPurchaseTypeChange: (purchaseType: PurchaseType) => void;
  onQuantityPickerChange: (e: React.FormEvent<HTMLInputElement>) => void;
  activeSubscriptionOption: SubscriptionOptionType;
  activePurchaseType: PurchaseType;
  subscribeSelected: boolean;
  topCallout?: string;
  bottomCallout?: string;
  descriptionCallout?: string;
  modalTitle?: string;
  modalButtonText?: string;
  listOfMeasuredItems?: Array<{
    title: string;
    image: string;
    items: string[];
  }>;
  hideSubscription?: boolean;
  heroSubscriptionFaqUrl?: string;
  ctaSubheadline: string;
  quantity: number;
  isOutOfStock: boolean;
  consultAvailable?: boolean;
  consultAvailableText: string;
  consultIcon: string;
  promo?: Promo;
  promotionable?: boolean;
  maxQuantity?: number;
  regionalRestrictionApplies?: boolean;
  hasPrePurchaseStep?: boolean;
  disclaimer: DisclaimerDataProps | null;
  screener: ScreenerDataProps | null;
  detailsBottomCallout?: string;
  badges: ProductBadgeConfig[];
  showRating: boolean;
  showNewAboveTheFoldPdp?: boolean;
};

type IfDetailsButtonCalloutProps = {
  detailsBottomCallout: string | undefined;
};

export const IfDetailsButtonCallout = ({
  detailsBottomCallout,
}: IfDetailsButtonCalloutProps): JSX.Element => (
  <>
    {detailsBottomCallout ? (
      <S.DetailsBottomCallout>{detailsBottomCallout}</S.DetailsBottomCallout>
    ) : null}
  </>
);

export const PDPHero = ({
  productId,
  name,
  price,
  memberPrice,
  memberHeroCopy,
  membershipVariantId,
  activeCredits,
  descriptionHeader,
  description,
  sliderImages,
  collectionMethods,
  measurements,
  onAddToCartClick,
  onExpressPayClick,
  subscriptionOptions,
  onSubscriptionOptionChange,
  activeSubscriptionOption,
  onPurchaseTypeChange,
  activePurchaseType,
  onQuantityPickerChange,
  subscribeSelected,
  averageRating,
  reviewCount,
  topCallout,
  bottomCallout,
  descriptionCallout,
  modalButtonText,
  modalTitle,
  listOfMeasuredItems,
  hideSubscription,
  heroSubscriptionFaqUrl,
  ctaSubheadline,
  quantity,
  isOutOfStock,
  consultAvailable,
  consultAvailableText,
  consultIcon,
  promo,
  promotionable,
  maxQuantity,
  regionalRestrictionApplies,
  hasPrePurchaseStep,
  disclaimer,
  screener,
  detailsBottomCallout,
  badges,
  showRating,
  showNewAboveTheFoldPdp = false,
}: PDPHeroProps): JSX.Element => {
  const [open, setOpen] = useState<boolean>(false);
  const heroRef = useRef<HTMLDivElement>(null);
  const [showSticky, setShowSticky] = useState(false);
  const { handleComparisonModalClick } = useContext(ComparisonModalContext);
  const { ref, inView, entry } = useInView();

  const productData = useContext(ProductInfoContext);
  const { productCategoryMap } = useProductCategories();
  const productCategory = Object.values(
    productCategoryMap as Record<string, ProductCategory>,
  ).find((category) =>
    category.product.some((product) => product.slug === productData.slug),
  );
  const crumbs = [
    {
      display: 'Shop All',
      url: '/products',
    },
  ];
  if (productCategory) {
    crumbs.push({
      display: productCategory.title,
      url: `/products/?category=${productCategory.pipId}`,
    });
  }

  const isFoodSensitivityTest = name === 'Food Sensitivity Test';

  const userHasMembershipForTest = !!memberPrice;
  const final = memberPrice?.replace(/\D/g, '') || price;
  const stripeTotal = subscribeSelected
    ? activeSubscriptionOption.price
    : final;

  const handleMeasuresModalClick = () => {
    setOpen(true);
    ewAnalytics.track({
      event: ANALYTICS.EVENTS.CLICKED_BUTTON,
      data: {
        label: 'Hero Measures Modal Link',
      },
    });
  };

  ewAnalytics.useAttachTrackingToLinks(heroRef);

  useEffect(() => {
    if (entry) {
      if (!inView) {
        if (entry.boundingClientRect.bottom < 0) {
          setShowSticky(true && !isOutOfStock && !regionalRestrictionApplies);
        }
      } else {
        setShowSticky(false);
      }
    }
  }, [entry, inView, isOutOfStock, regionalRestrictionApplies]);

  return (
    <S.HeroWrapper ref={heroRef}>
      {topCallout ? (
        <S.Callout data-testid="topCallout">
          <span
            dangerouslySetInnerHTML={{
              __html: sanitize(topCallout),
            }}
          />
        </S.Callout>
      ) : null}
      {showSticky ? (
        <S.StickyOrderBarContainer data-testid="stickyAddToCart">
          <S.StickyContent>
            <S.StickyProductName>{name}</S.StickyProductName>
            <PurchaseControls
              onPurchaseTypeChange={onPurchaseTypeChange}
              selectedPurchaseType={activePurchaseType}
              subscriptionOptions={subscriptionOptions}
              onQuantityPickerChange={onQuantityPickerChange}
              quantity={quantity}
              onSubscriptionOptionChange={onSubscriptionOptionChange}
              selectedSubscriptionOption={activeSubscriptionOption}
              onAddToCartClick={onAddToCartClick}
              activeCredits={activeCredits}
              membershipVariantId={membershipVariantId}
              isStickyAppearance
              hideSubscription={hideSubscription}
              heroSubscriptionFaqUrl={heroSubscriptionFaqUrl}
              onExpressPayClick={onExpressPayClick}
              stripeTotal={stripeTotal}
              ctaSubheadline={ctaSubheadline}
              maxQuantity={maxQuantity}
              hasPrePurchaseStep={hasPrePurchaseStep}
              disclaimer={disclaimer}
              screener={screener}
              showNewAboveTheFoldPdp={showNewAboveTheFoldPdp || false}
            />
          </S.StickyContent>
        </S.StickyOrderBarContainer>
      ) : null}
      <S.Section data-testid="pdpHero">
        {/* New "Above the fold" Design Updates only in Mobile/Tablet (NP1-337 - NP1-340) */}
        <S.ShowForMobileAndTablet
          showNewAboveTheFoldPdp={showNewAboveTheFoldPdp || false}
        >
          <S.Container>
            <S.Row>
              <Breadcrumbs
                crumbs={crumbs}
                current={name}
                showNewAboveTheFoldPdp={showNewAboveTheFoldPdp || false}
              />
            </S.Row>
            <S.Content>
              <S.SliderWrapper
                showNewAboveTheFoldPdp={showNewAboveTheFoldPdp || false}
              >
                <S.SliderContainer
                  showNewAboveTheFoldPdp={showNewAboveTheFoldPdp || false}
                >
                  <div data-testid={PDP.HERO_SWIPER}>
                    <PDPSwiperWrapper
                      sliderImages={sliderImages}
                      showNewAboveTheFoldPdp={showNewAboveTheFoldPdp || false}
                    />
                  </div>
                </S.SliderContainer>
              </S.SliderWrapper>
              <S.InfoSectionWrapper
                showNewAboveTheFoldPdp={showNewAboveTheFoldPdp || false}
              >
                <S.InfoSection>
                  <S.BadgesWrapper>
                    {badges.map((badge) => (
                      <Badge
                        key={badge.text}
                        backgroundColor={badge.color}
                        text={badge.text}
                      />
                    ))}
                  </S.BadgesWrapper>
                  <S.ProductName
                    data-testid="heroProductName"
                    showNewAboveTheFoldPdp={showNewAboveTheFoldPdp || false}
                  >
                    {name}
                  </S.ProductName>
                  <Pricing
                    price={parseFloat(price)}
                    activeSubscriptionOption={activeSubscriptionOption}
                    promo={promo}
                    productId={productId}
                    promotionable={promotionable}
                    memberPrice={memberPrice}
                    subscribeSelected={subscribeSelected}
                    memberHeroCopy={memberHeroCopy}
                    isOutOfStock={isOutOfStock}
                    showNewAboveTheFoldPdp={showNewAboveTheFoldPdp || false}
                  />
                  {reviewCount && showRating ? (
                    <AnchorLink offset={size.xl5} href="#reviewsSection">
                      <S.Stars
                        rating={averageRating}
                        numOfReviews={reviewCount}
                        showNewAboveTheFoldPdp={showNewAboveTheFoldPdp || false}
                      />
                    </AnchorLink>
                  ) : null}
                  <S.PurchaseSection
                    ref={ref}
                    showNewAboveTheFoldPdp={showNewAboveTheFoldPdp || false}
                  >
                    {isOutOfStock ? (
                      <S.ProductDescription
                        data-testid="outOfStockMessage"
                        showNewAboveTheFoldPdp={showNewAboveTheFoldPdp || false}
                      >
                        This test is currently unavailable.
                      </S.ProductDescription>
                    ) : (
                      <>
                        {regionalRestrictionApplies ? (
                          <S.ProductDescription
                            data-testid="outOfStockMessage"
                            showNewAboveTheFoldPdp={
                              showNewAboveTheFoldPdp || false
                            }
                          >
                            This test is not available in New York state.
                          </S.ProductDescription>
                        ) : (
                          <PurchaseControls
                            onPurchaseTypeChange={onPurchaseTypeChange}
                            selectedPurchaseType={activePurchaseType}
                            subscriptionOptions={subscriptionOptions}
                            onQuantityPickerChange={onQuantityPickerChange}
                            quantity={quantity}
                            onSubscriptionOptionChange={
                              onSubscriptionOptionChange
                            }
                            selectedSubscriptionOption={
                              activeSubscriptionOption
                            }
                            onAddToCartClick={onAddToCartClick}
                            activeCredits={activeCredits}
                            membershipVariantId={membershipVariantId}
                            hideSubscription={hideSubscription}
                            heroSubscriptionFaqUrl={heroSubscriptionFaqUrl}
                            onExpressPayClick={onExpressPayClick}
                            stripeTotal={stripeTotal}
                            ctaSubheadline={ctaSubheadline}
                            maxQuantity={maxQuantity}
                            hasPrePurchaseStep={hasPrePurchaseStep}
                            screener={screener}
                            disclaimer={disclaimer}
                            showNewAboveTheFoldPdp={
                              showNewAboveTheFoldPdp || false
                            }
                            source="mobile"
                          />
                        )}
                      </>
                    )}
                  </S.PurchaseSection>
                  <S.DescriptionContent
                    showNewAboveTheFoldPdp={showNewAboveTheFoldPdp || false}
                  >
                    <S.ProductDescriptionWrapper>
                      <S.ProductDescription
                        showNewAboveTheFoldPdp={showNewAboveTheFoldPdp || false}
                      >
                        <span
                          dangerouslySetInnerHTML={{
                            __html: sanitize(description),
                          }}
                        />
                      </S.ProductDescription>
                    </S.ProductDescriptionWrapper>
                    {listOfMeasuredItems &&
                    listOfMeasuredItems.length &&
                    modalButtonText &&
                    measurements[0].name &&
                    modalTitle ? (
                      <>
                        <S.MeasuresWrapper
                          showNewAboveTheFoldPdp={
                            showNewAboveTheFoldPdp || false
                          }
                        >
                          <S.MeasuresText
                            iconUrl={measurements[0].iconUrl}
                            name={measurements[0].name}
                            isHTML
                            data-testid="heroMeasuresText"
                          />
                          <S.ModalButton onClick={handleMeasuresModalClick}>
                            {modalButtonText}
                          </S.ModalButton>
                        </S.MeasuresWrapper>
                        <MeasuredItemsModal
                          modalTitle={modalTitle}
                          listOfItems={listOfMeasuredItems}
                          open={open}
                          openHandler={setOpen}
                        />
                      </>
                    ) : measurements[0].name ? (
                      <S.MeasuresWrapper
                        showNewAboveTheFoldPdp={showNewAboveTheFoldPdp || false}
                      >
                        <S.MeasuresText
                          iconUrl={measurements[0].iconUrl}
                          name={measurements[0].name}
                          isHTML
                          data-testid="heroMeasuresText"
                        />
                      </S.MeasuresWrapper>
                    ) : null}
                    <S.CollectionMethods
                      data-testid="heroCollectionMethods"
                      showNewAboveTheFoldPdp={showNewAboveTheFoldPdp || false}
                    >
                      {[...collectionMethods].map((method) => (
                        <S.CollectionMethod
                          key={method.name}
                          iconUrl={method.iconUrl}
                          name={method.name}
                        />
                      ))}
                    </S.CollectionMethods>
                    {isFoodSensitivityTest ? (
                      <S.FscUpgradeLinkWrapper
                        showNewAboveTheFoldPdp={showNewAboveTheFoldPdp || false}
                      >
                        <S.MeasuresText
                          iconUrl={star}
                          name="Test a"
                          isHTML
                          data-testid="heroComparisonText"
                        />
                        <S.ModalButton onClick={handleComparisonModalClick}>
                          larger variety of foods
                        </S.ModalButton>
                      </S.FscUpgradeLinkWrapper>
                    ) : null}
                    {consultAvailable ? (
                      <S.ConsultWrapper>
                        <S.ConsultText
                          iconUrl={consultIcon}
                          name={consultAvailableText}
                          isHTML
                          data-testid="heroConsultAvailable"
                        />
                      </S.ConsultWrapper>
                    ) : null}
                  </S.DescriptionContent>
                </S.InfoSection>
                {descriptionCallout && !userHasMembershipForTest ? (
                  <S.DescriptionCallout data-testid="descriptionCallout">
                    <span
                      dangerouslySetInnerHTML={{
                        __html: sanitize(descriptionCallout),
                      }}
                    />
                  </S.DescriptionCallout>
                ) : null}
              </S.InfoSectionWrapper>
            </S.Content>
            <IfDetailsButtonCallout
              detailsBottomCallout={detailsBottomCallout}
            />
          </S.Container>
        </S.ShowForMobileAndTablet>
        {/* Original PDP Design for Desktop */}
        <S.ShowForDesktop
          showNewAboveTheFoldPdp={showNewAboveTheFoldPdp || false}
        >
          <Container>
            <Row>
              <Col>
                <Breadcrumbs
                  crumbs={crumbs}
                  current={name}
                  showNewAboveTheFoldPdp={showNewAboveTheFoldPdp || false}
                />
              </Col>
            </Row>
            <Row>
              <S.Content>
                <S.ProductDescriptionHeader>
                  {descriptionHeader}
                </S.ProductDescriptionHeader>
                <S.SliderWrapper
                  showNewAboveTheFoldPdp={showNewAboveTheFoldPdp || false}
                >
                  <S.SliderContainer
                    showNewAboveTheFoldPdp={showNewAboveTheFoldPdp || false}
                  >
                    <div data-testid={PDP.HERO_SWIPER}>
                      <PDPSwiperWrapper
                        sliderImages={sliderImages}
                        showNewAboveTheFoldPdp={showNewAboveTheFoldPdp || false}
                      />
                    </div>
                  </S.SliderContainer>
                </S.SliderWrapper>
                <S.InfoSectionWrapper
                  showNewAboveTheFoldPdp={showNewAboveTheFoldPdp || false}
                >
                  <S.InfoSection>
                    <S.BadgesWrapper>
                      {badges.map((badge) => (
                        <Badge
                          key={badge.text}
                          backgroundColor={badge.color}
                          text={badge.text}
                        />
                      ))}
                    </S.BadgesWrapper>
                    <S.ProductName
                      data-testid="heroProductName"
                      showNewAboveTheFoldPdp={showNewAboveTheFoldPdp || false}
                    >
                      {name}
                    </S.ProductName>
                    {reviewCount && showRating ? (
                      <AnchorLink offset={size.xl5} href="#reviewsSection">
                        <S.Stars
                          rating={averageRating}
                          numOfReviews={reviewCount}
                          showNewAboveTheFoldPdp={
                            showNewAboveTheFoldPdp || false
                          }
                        />
                      </AnchorLink>
                    ) : null}
                    <S.DescriptionContent
                      showNewAboveTheFoldPdp={showNewAboveTheFoldPdp || false}
                    >
                      <S.ProductDescription
                        showNewAboveTheFoldPdp={showNewAboveTheFoldPdp || false}
                      >
                        <span
                          dangerouslySetInnerHTML={{
                            __html: sanitize(description),
                          }}
                        />
                      </S.ProductDescription>
                      {listOfMeasuredItems &&
                      listOfMeasuredItems.length &&
                      modalButtonText &&
                      measurements[0].name &&
                      modalTitle ? (
                        <>
                          <S.MeasuresWrapper
                            showNewAboveTheFoldPdp={
                              showNewAboveTheFoldPdp || false
                            }
                          >
                            <S.MeasuresText
                              iconUrl={measurements[0].iconUrl}
                              name={measurements[0].name}
                              isHTML
                              data-testid="heroMeasuresText"
                            />
                            <S.ModalButton onClick={handleMeasuresModalClick}>
                              {modalButtonText}
                            </S.ModalButton>
                          </S.MeasuresWrapper>
                          <MeasuredItemsModal
                            modalTitle={modalTitle}
                            listOfItems={listOfMeasuredItems}
                            open={open}
                            openHandler={setOpen}
                          />
                        </>
                      ) : measurements[0].name ? (
                        <S.MeasuresWrapper
                          showNewAboveTheFoldPdp={
                            showNewAboveTheFoldPdp || false
                          }
                        >
                          <S.MeasuresText
                            iconUrl={measurements[0].iconUrl}
                            name={measurements[0].name}
                            isHTML
                            data-testid="heroMeasuresText"
                          />
                        </S.MeasuresWrapper>
                      ) : null}
                      <S.CollectionMethods
                        data-testid="heroCollectionMethods"
                        showNewAboveTheFoldPdp={showNewAboveTheFoldPdp || false}
                      >
                        {[...collectionMethods].map((method) => (
                          <S.CollectionMethod
                            key={method.name}
                            iconUrl={method.iconUrl}
                            name={method.name}
                          />
                        ))}
                      </S.CollectionMethods>
                      {isFoodSensitivityTest ? (
                        <S.FscUpgradeLinkWrapper
                          showNewAboveTheFoldPdp={
                            showNewAboveTheFoldPdp || false
                          }
                        >
                          <S.MeasuresText
                            iconUrl={star}
                            name="Test a"
                            isHTML
                            data-testid="heroComparisonText"
                          />
                          <S.ModalButton onClick={handleComparisonModalClick}>
                            larger variety of foods
                          </S.ModalButton>
                        </S.FscUpgradeLinkWrapper>
                      ) : null}
                      {consultAvailable ? (
                        <S.ConsultWrapper>
                          <S.ConsultText
                            iconUrl={consultIcon}
                            name={consultAvailableText}
                            isHTML
                            data-testid="heroConsultAvailable"
                          />
                        </S.ConsultWrapper>
                      ) : null}
                    </S.DescriptionContent>
                  </S.InfoSection>
                  <Pricing
                    price={parseFloat(price)}
                    activeSubscriptionOption={activeSubscriptionOption}
                    promo={promo}
                    productId={productId}
                    promotionable={promotionable}
                    memberPrice={memberPrice}
                    subscribeSelected={subscribeSelected}
                    memberHeroCopy={memberHeroCopy}
                    isOutOfStock={isOutOfStock}
                    showNewAboveTheFoldPdp={showNewAboveTheFoldPdp || false}
                  />
                  {descriptionCallout && !userHasMembershipForTest ? (
                    <S.DescriptionCallout data-testid="descriptionCallout">
                      <span
                        dangerouslySetInnerHTML={{
                          __html: sanitize(descriptionCallout),
                        }}
                      />
                    </S.DescriptionCallout>
                  ) : null}
                  <S.PurchaseSection
                    ref={ref}
                    showNewAboveTheFoldPdp={showNewAboveTheFoldPdp || false}
                  >
                    {isOutOfStock ? (
                      <S.ProductDescription
                        data-testid="outOfStockMessage"
                        showNewAboveTheFoldPdp={showNewAboveTheFoldPdp || false}
                      >
                        This test is currently unavailable.
                      </S.ProductDescription>
                    ) : (
                      <>
                        {regionalRestrictionApplies ? (
                          <S.ProductDescription
                            data-testid="outOfStockMessage"
                            showNewAboveTheFoldPdp={
                              showNewAboveTheFoldPdp || false
                            }
                          >
                            This test is not available in New York state.
                          </S.ProductDescription>
                        ) : (
                          <PurchaseControls
                            onPurchaseTypeChange={onPurchaseTypeChange}
                            selectedPurchaseType={activePurchaseType}
                            subscriptionOptions={subscriptionOptions}
                            onQuantityPickerChange={onQuantityPickerChange}
                            quantity={quantity}
                            onSubscriptionOptionChange={
                              onSubscriptionOptionChange
                            }
                            selectedSubscriptionOption={
                              activeSubscriptionOption
                            }
                            onAddToCartClick={onAddToCartClick}
                            activeCredits={activeCredits}
                            membershipVariantId={membershipVariantId}
                            hideSubscription={hideSubscription}
                            heroSubscriptionFaqUrl={heroSubscriptionFaqUrl}
                            onExpressPayClick={onExpressPayClick}
                            stripeTotal={stripeTotal}
                            ctaSubheadline={ctaSubheadline}
                            maxQuantity={maxQuantity}
                            hasPrePurchaseStep={hasPrePurchaseStep}
                            screener={screener}
                            disclaimer={disclaimer}
                            showNewAboveTheFoldPdp={
                              showNewAboveTheFoldPdp || false
                            }
                            source="desktop"
                          />
                        )}
                      </>
                    )}
                  </S.PurchaseSection>
                </S.InfoSectionWrapper>
              </S.Content>
              <IfDetailsButtonCallout
                detailsBottomCallout={detailsBottomCallout}
              />
            </Row>
          </Container>
        </S.ShowForDesktop>
        {bottomCallout ? (
          <Themable>
            {/* @ts-ignore - the theme prop is only used for Themeable to find and filter */}
            <S.BottomCallout theme={Theme.Default} data-testid="bottomCallout">
              <span
                dangerouslySetInnerHTML={{
                  __html: sanitize(bottomCallout),
                }}
              />
            </S.BottomCallout>
            <S.BottomCallout
              // @ts-ignore - the theme prop is only used for Themeable to find and filter
              theme={Theme.Winter2020}
              data-testid="bottomCallout"
            >
              <S.BottomCalloutImgL src={lightsLeft} />
              <span
                dangerouslySetInnerHTML={{
                  __html: sanitize(bottomCallout),
                }}
              />
              <S.BottomCalloutImgR src={lightsRight} />
            </S.BottomCallout>
          </Themable>
        ) : null}
      </S.Section>
    </S.HeroWrapper>
  );
};

export default PDPHero;
